// noinspection ExceptionCaughtLocallyJS

import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { getApiUrl } from "#/constants/environment";
import { toast } from "#next/lib/toast";

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || "");

export interface UseStripeCheckoutProps {
  priceIds: string[]
}

export function useStripeCheckout(args: UseStripeCheckoutProps) {
  const [ isLoading, setIsLoading ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);

  const handleCheckout = async () => {
    // const priceIds = handleArgs?.priceIds ?? args?.priceIds;
    // if (!priceIds || priceIds.length === 0) {
    //   throw Logger.error({
    //     error: new Error("No price IDs provided for checkout")
    //   });
    // }
    const priceIds = args?.priceIds;
    try {
      setIsLoading(true);
      setError(null);

      // Call your API to create a checkout session
      const response = await fetch(`${getApiUrl()}/api/stripe/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ priceIds })
      });

      const { sessionId, error: apiError } = await response.json();

      if (apiError) {
        throw new Error(apiError);
      }

      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error("Failed to load Stripe");
      }

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId
      });

      if (stripeError) {
        throw new Error(stripeError.message || "An error occurred during checkout");
      }
    } catch (err: any) {
      toast.error("Checkout failed", err);
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    handleCheckout,
    isLoading,
    error
  };
}

export interface UseSubscriptionManagementProps {
  onSuccess?: (message: string) => void,
  onError?: (error: string) => void
}

export function useSubscriptionManagement({
  onSuccess, onError
}: UseSubscriptionManagementProps = {}) {
  const [ isLoading, setIsLoading ] = useState(false);

  const handleAction = async (action: "cancel" | "reactivate" | "portal") => {
    try {
      setIsLoading(true);

      const response = await fetch("/api/stripe/subscription-management", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"

        },
        body: JSON.stringify({ action })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "An error occurred");
      }

      if (action === "portal" && data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
        return;
      }

      onSuccess?.(data.message || "Operation successful");
      return data;
    } catch (err: any) {
      toast.error("Subscription management failed", err);
      onError?.(err.message || "An error occurred");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    cancelSubscription: () => handleAction("cancel"),
    reactivateSubscription: () => handleAction("reactivate"),
    goToCustomerPortal: () => handleAction("portal"),
    isLoading
  };
}